import { Controller } from "@hotwired/stimulus";
import {
  iconNamesFromClassList,
  iconNamesForControl,
  controlLabel,
} from "lib/pageCaptureLabels.mjs";
import { captureDecision } from "lib/screenshotTargeting.mjs";

// Captures the user's visible page content as structured markdown text.
// Sends it to the server for the LLM to analyze — more reliable than
// image-based capture (no CORS/font issues) and more useful to the LLM
// since it can read text perfectly but struggles with text-in-images.
//
// The trade that buys: Kit sees exactly what this walk emits and nothing else,
// so an affordance we skip is one Kit will report as absent from the customer's
// screen. Naming rules for text-less controls live in lib/pageCaptureLabels.mjs
// — see the note there for the two conversations that went wrong without them.
export default class extends Controller {
  static values = {
    requestId: Number,
    uploadUrl: String,
    // The page the customer's latest message was sent from (Message#page_url).
    targetPath: String,
  };

  // How long a tab that is NOT on the target page waits before answering on
  // behalf of a customer who has since navigated. Long enough for the tab that
  // IS on the target page to win.
  static FALLBACK_DELAY_MS = 1500;

  // Only the tab the customer is on answers — see lib/screenshotTargeting.mjs.
  connect() {
    this._requestId = this.requestIdValue;
    this._uploadUrl = this.uploadUrlValue;
    this._element = this.element;

    const decision = captureDecision({
      targetPath: this.hasTargetPathValue ? this.targetPathValue : "",
      pathname: window.location.pathname,
      visibilityState: document.visibilityState,
    });

    if (decision === "now") {
      this.capturePageContent();
    } else if (decision === "later") {
      this._timer = setTimeout(
        () => this.capturePageContent(),
        this.constructor.FALLBACK_DELAY_MS
      );
    } else {
      this.removeElement();
    }
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer);
  }

  removeElement() {
    try {
      this._element.remove();
    } catch (e) {
      /* already removed */
    }
  }

  async capturePageContent() {
    try {
      const markdown = this.domToMarkdown();
      const blob = new Blob([markdown], { type: "text/plain" });
      await this.uploadContent(blob);
    } catch (error) {
      console.error("[Kit Screenshot] Capture failed:", error);
      await this.reportFailure();
    } finally {
      this.removeElement();
    }
  }

  domToMarkdown() {
    const lines = [];
    const url = window.location.pathname;
    const title = document.title;

    lines.push(`# ${title}`);
    lines.push(`**URL:** ${url}`);
    lines.push(`**Viewport:** ${window.innerWidth}x${window.innerHeight}`);
    lines.push("");

    // Walk the main content area, excluding the Kit chat panel
    const panel = document.getElementById("kit-chat-panel");
    const body = document.body;

    const walk = (node, depth) => {
      if (node === panel) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) lines.push(text);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const el = node;
      const style = window.getComputedStyle(el);
      // Skip hidden elements
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        el.classList.contains("hidden")
      )
        return;
      // Skip script/style/svg
      const tag = el.tagName.toLowerCase();
      if (["script", "style", "svg", "noscript", "iframe"].includes(tag))
        return;

      // Format headings
      if (tag.match(/^h[1-6]$/)) {
        const level = parseInt(tag[1]);
        const text = el.textContent.trim();
        if (text) lines.push(`${"#".repeat(level)} ${text}`);
        return;
      }

      // Format links
      if (tag === "a" && el.href) {
        const label = this.labelFor(el);
        if (label && label.length < 100) {
          lines.push(`[${label}](${el.pathname || el.href})`);
          return;
        }
      }

      // Format images
      if (tag === "img") {
        const alt = el.alt || el.title || "image";
        lines.push(`![${alt}]`);
        return;
      }

      // Format buttons
      if (tag === "button" || (tag === "input" && el.type === "submit")) {
        const label = this.labelFor(el);
        if (label) lines.push(`[Button: ${label}]`);
        return;
      }

      // A bare icon that is not itself a control still marks state a customer
      // can see and ask about — a red heart meaning "already saved", a warning
      // triangle on a row. Emitted only when it stands alone, since a labelled
      // control has already named its own icon by the time we get here.
      if (tag === "i" && !el.textContent.trim()) {
        const icons = iconNamesFromClassList(el.className);
        if (icons.length) lines.push(`[${icons.join(" ")} icon]`);
        return;
      }

      // Format inputs
      if (tag === "input" || tag === "textarea" || tag === "select") {
        const label =
          el.placeholder || el.ariaLabel || el.name || el.type || "input";
        const val = el.value || "";
        lines.push(`[${tag}: ${label}${val ? " = " + val : ""}]`);
        return;
      }

      // Format tables
      if (tag === "table") {
        const rows = el.querySelectorAll("tr");
        rows.forEach((row) => {
          const cells = Array.from(row.querySelectorAll("td, th"))
            .map((c) => c.textContent.trim())
            .filter(Boolean);
          if (cells.length) lines.push("| " + cells.join(" | ") + " |");
        });
        return;
      }

      // Format list items
      if (tag === "li") {
        const text = el.textContent.trim();
        if (text && text.length < 200) {
          lines.push(`- ${text}`);
          return;
        }
      }

      // Recurse into children
      for (const child of el.childNodes) {
        walk(child, depth + 1);
      }

      // Add spacing after block elements
      if (["div", "section", "article", "p", "ul", "ol"].includes(tag)) {
        if (lines.length && lines[lines.length - 1] !== "") lines.push("");
      }
    };

    walk(body, 0);

    // Deduplicate consecutive identical lines and limit length
    const result = [];
    let prev = null;
    for (const line of lines) {
      if (line !== prev || line === "") {
        result.push(line);
        prev = line;
      }
    }

    return result.join("\n").substring(0, 5000);
  }

  // What to call a link or button in the transcript. Text when it has any;
  // otherwise the accessible name the author gave it, and failing that the icon
  // it renders — which for most of this app's icon-only controls is the only
  // identity there is.
  labelFor(el) {
    return controlLabel({
      text: el.textContent,
      ariaLabel: el.getAttribute("aria-label"),
      title: el.getAttribute("title") || el.dataset.tooltip,
      alt: el.value,
      iconNames: this.iconNamesWithin(el),
    });
  }

  // The glyph a control draws — the first `<i>` it wraps, which is what
  // `button_to ... do %i.icon.heart` compiles to. An `<i>` used as a control
  // itself names its own.
  //
  // Never the control's own class list: Fomantic writes `icon` on the CONTROL as
  // a layout class, so `<button class="ui button secondary icon">` would name
  // itself "button secondary". See iconNamesForControl.
  iconNamesWithin(el) {
    const descendant = el.matches("i[class*='icon']")
      ? el
      : el.querySelector("i[class*='icon']");

    return iconNamesForControl({
      tagName: el.tagName,
      className: el.className,
      descendantIconClassName: descendant ? descendant.className : "",
    });
  }

  async uploadContent(blob) {
    const formData = new FormData();
    formData.append("image", blob, "page-content.txt");
    formData.append("screenshot_request_id", this._requestId);

    const csrfToken = this.getCsrfToken();

    const response = await fetch(this._uploadUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: formData,
    });

    if (!response.ok) {
      console.error("[Kit Screenshot] Upload failed:", response.status);
      await this.reportFailure();
    }
  }

  getCsrfToken() {
    var meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }

  async reportFailure() {
    try {
      const formData = new FormData();
      formData.append("screenshot_request_id", this._requestId);
      const csrfToken = this.getCsrfToken();

      await fetch(this._uploadUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: formData,
      });
    } catch (e) {
      // Best effort — server-side timeout will handle cleanup
    }
  }
}
